'use client';

import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { useUsernameUpdate } from '@sola/my-sdk/settings/hooks.ts';
import type { UserError } from '@sola/shared-utils/errors-user.ts';

import {
  LocalAlertsProvider,
  useLocalAlerts,
} from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import {
  createUsernameUpdateErrorAlert,
  createUsernameUpdateSuccessAlert,
} from '../../utils/alerts.tsx';

const UsernameModalForm = ({
  defaultFormValues,
  onSuccess,
}: {
  defaultFormValues: { username: string };
  onSuccess: () => void;
}) => {
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const { mutateAsync: updateUsername } = useUsernameUpdate();
  const [isBusy, setIsBusy] = useState(false);
  const { t } = useTranslation(['core', 'my-settings']);

  const handleSubmit = async (data: FieldValues) => {
    setIsBusy(true);
    clearAlerts();
    try {
      await updateUsername({
        preferredUsername: data.username as string,
      });
      onSuccess();
    } catch (error) {
      const errorType = (error as UserError)?.type;
      addAlerts(createUsernameUpdateErrorAlert(errorType));
      setIsBusy(false);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      options={{
        defaultValues: defaultFormValues,
        mode: 'onChange',
      }}
    >
      <InputField
        key="username"
        name="username"
        label={t('my-settings:form.username.usernameLabel')}
        required
      />
      <DialogActions>
        <DialogActionClose>{t('core:actions.cancel')}</DialogActionClose>
        <DialogActionPrimary type="submit" isBusy={isBusy} disableUntilFilled>
          {t('core:actions.save')}
        </DialogActionPrimary>
      </DialogActions>
    </Form>
  );
};

export const SettingsModalUsername = ({
  username,
  openTrigger,
}: {
  username: string;
  openTrigger: React.ReactElement<HTMLButtonElement>;
}) => {
  const sectionAlerts = useLocalAlerts();
  const [isOpen, setIsOpen] = useState(false);

  const defaultFormValues = {
    username: username,
  };

  const { t } = useTranslation(['core', 'my-settings']);

  const handleSuccess = () => {
    sectionAlerts.addAlerts(createUsernameUpdateSuccessAlert());
    setIsOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    sectionAlerts.clearAlerts();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent
        icon="user-edit"
        title={t('my-settings:form.username.title')}
      >
        <LocalAlertsProvider>
          <LocalAlerts />
          <UsernameModalForm
            defaultFormValues={defaultFormValues}
            onSuccess={handleSuccess}
          />
        </LocalAlertsProvider>
      </DialogContent>
    </Dialog>
  );
};
